import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContentInputSchema } from "@dawa/types";
import { supabase } from "../lib/supabase";
import {
  Input,
  Button,
  Select,
  Card,
  CardHeader,
  CardContent,
  LoadingOverlay,
  useToast,
  AlertModal,
  PageLayout,
  PageHeader,
  FormField,
} from "@dawa/ui";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { z } from "zod";
import { useCredits } from "../hooks/useCredits";
import { motion } from "framer-motion";
import {
  Type,
  List,
  Palette,
  Users,
  Ruler,
  Briefcase,
  Plus,
  User,
  Image as ImageIcon,
  Settings,
} from "lucide-react";
import { CreateContentSchema, type CreateContent } from "../schemas/content";

const S = ContentInputSchema;
const FORM_STORAGE_KEY = "content-new-form";

const DEFAULT_FORM_VALUES: z.infer<typeof S> = {
  primaryKeyword: "",
  secondaryKeywords: [],
  language: "fr",
  searchIntent: "informational",
  persona: undefined,
  tone: "neutral",
  length: "medium",
  structure: { enforceH1: true, maxDepth: 3 },
  brandConstraints: "",
  internalLinksCandidates: [],
  targetCountry: "FR",
  eeatProofs: [],
  contentType: "article",
  withFaq: false,
  useFormatting: false,
  imageCount: 0,
  autoMeta: false,
  targetEntities: [],
  competitorUrls: [],
  serpFeatures: [],
  cta: "",
  readingLevel: "pro",
  requiredSections: [],
  forbidden: [],
  wordCount: {},
};

const INTENT_LABELS: Record<string, string> = {
  informational: "Information",
  transactional: "Transaction",
  navigational: "Navigation",
  commercial: "Commercial",
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  article: "Article",
  landing: "Landing page",
};

const SERP_FEATURE_LABELS: Record<string, string> = {
  featured_snippet: "Featured snippet",
  paa: "People Also Ask",
  images: "Images",
  videos: "Vidéos",
  news: "Actualités",
};

const READING_LEVEL_LABELS: Record<string, string> = {
  simple: "Lecture facile",
  pro: "Professionnel",
  expert: "Expert",
};

const parseListInput = (value: string) =>
  value
    .split(/[\n,;]/)
    .map((x) => x.trim())
    .filter(Boolean);

const joinListInput = (value?: string[]) => (Array.isArray(value) ? value.join(", ") : "");

const getIntentLabel = (intent: unknown) =>
  typeof intent === "string" ? (INTENT_LABELS[intent] ?? intent) : "";

const getContentTypeLabel = (type: unknown) =>
  typeof type === "string" ? (CONTENT_TYPE_LABELS[type] ?? type) : "";

const getSerpFeatureLabel = (feature: string) => SERP_FEATURE_LABELS[feature] ?? feature;

const getReadingLevelLabel = (level: unknown) =>
  typeof level === "string" ? (READING_LEVEL_LABELS[level] ?? level) : "";

// --- Playbooks (Phase 3) ---
const PLAYBOOKS = [
  {
    id: "seo-quick-article",
    label: "Article SEO – 15 min",
    apply: (setValue: any) => {
      setValue("searchIntent", "informational");
      setValue("contentType", "article");
      setValue("withFaq", true);
      setValue("useFormatting", true);
      setValue("imageCount", 2);
    },
  },
  {
    id: "product-landing",
    label: "Landing produit",
    apply: (setValue: any) => {
      setValue("searchIntent", "transactional");
      setValue("contentType", "landing");
      setValue("withFaq", false);
      setValue("useFormatting", true);
      setValue("imageCount", 3);
    },
  },
  {
    id: "long-form",
    label: "Long‑form approfondi",
    apply: (setValue: any) => {
      setValue("searchIntent", "informational");
      setValue("contentType", "article");
      setValue("withFaq", true);
      setValue("useFormatting", true);
      setValue("imageCount", 0);
    },
  },
];

const LANGUAGES = {
  fr: { label: "Français", flag: "🇫🇷" },
  en: { label: "Anglais", flag: "🇬🇧" },
  es: { label: "Espagnol", flag: "🇪🇸" },
  de: { label: "Allemand", flag: "🇩🇪" },
} as const;

const COUNTRIES = {
  FR: { label: "France", flag: "🇫🇷" },
  US: { label: "États-Unis", flag: "🇺🇸" },
  ES: { label: "Espagne", flag: "🇪🇸" },
  CA: { label: "Canada", flag: "🇨🇦" },
} as const;

export default function ContentNew() {
  const nav = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof S>>({
    resolver: zodResolver(S),
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const [personas, setPersonas] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [personaId, setPersonaId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { credits, refresh } = useCredits();
  const [step, setStep] = useState(0);
  const steps = ["Contenu", "Options", "Prévisualisation"];
  const { addToast, Toasts } = useToast();
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [playbook, setPlaybook] = useState<string>("");
  const values = watch();
  const [serpAuto, setSerpAuto] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const notifyStepValidation = (label: string, isValid: boolean) => {
    if (isValid) {
      addToast(`Étape « ${label} » validée`, "success");
      return;
    }
    addToast(`Merci de corriger les erreurs dans l’étape « ${label} »`, "error");
  };
  const handleSubmitError = (message: string) => {
    setSubmitError(message);
    addToast(message, "error");
  };
  const hasAdvancedValues = useMemo(
    () =>
      Boolean(values.withFaq) ||
      Boolean(values.useFormatting) ||
      Boolean(values.autoMeta) ||
      (values.targetEntities?.length ?? 0) > 0 ||
      (values.competitorUrls?.length ?? 0) > 0 ||
      (values.serpFeatures?.length ?? 0) > 0 ||
      (values.requiredSections?.length ?? 0) > 0 ||
      (values.forbidden?.length ?? 0) > 0 ||
      Boolean(values.cta) ||
      Boolean(values.wordCount?.min) ||
      Boolean(values.wordCount?.max),
    [values],
  );

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId),
    [projects, projectId],
  );

  useEffect(() => {
    if (!showAdvanced && hasAdvancedValues) {
      setShowAdvanced(true);
    }
  }, [showAdvanced, hasAdvancedValues]);

  useEffect(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const formValues =
          typeof parsed?.form === "object" && parsed.form !== null ? parsed.form : parsed;
        if (formValues && typeof formValues === "object") {
          Object.entries(formValues).forEach(([k, v]) => setValue(k as any, v as any));
          const personaValue = (formValues as any).persona;
          if (personaValue?.id) setPersonaId(personaValue.id);
        }
        const meta =
          typeof parsed?.meta === "object" && parsed.meta !== null ? parsed.meta : undefined;
        const storedProjectId = meta?.projectId ?? (parsed as any).projectId;
        if (typeof storedProjectId === "string") setProjectId(storedProjectId);
        const storedPersonaId = meta?.personaId ?? (parsed as any).personaId;
        if (typeof storedPersonaId === "string") setPersonaId(storedPersonaId);
        const storedSerpAuto = meta?.serpAuto;
        if (typeof storedSerpAuto === "boolean") setSerpAuto(storedSerpAuto);
      } catch {
        /* ignore */
      }
    }
    setInitialized(true);
  }, [setValue]);

  useEffect(() => {
    if (initialized) {
      try {
        localStorage.setItem(
          FORM_STORAGE_KEY,
          JSON.stringify({
            form: values,
            meta: { projectId, personaId, serpAuto },
          }),
        );
      } catch {
        /* ignore */
      }
    }
  }, [values, initialized, projectId, personaId, serpAuto]);

  useEffect(() => {
    (async () => {
      const s = await supabase.auth.getSession();
      const t = s.data.session?.access_token;
      if (!t) return;
      setToken(t);
      const [persRes, projRes] = await Promise.all([
        apiFetch("/v1/personas", {
          headers: { Authorization: `Bearer ${t}` },
        }),
        apiFetch("/v1/projects", {
          headers: { Authorization: `Bearer ${t}` },
        }),
      ]);
      const persRaw = await persRes.json().catch(() => []);
      const projRaw = await projRes.json().catch(() => []);
      const persData = Array.isArray(persRaw) ? persRaw : [];
      const projData = Array.isArray(projRaw) ? projRaw : [];
      if (!Array.isArray(persRaw)) addToast("Échec du chargement des personas", "error");
      if (!Array.isArray(projRaw)) addToast("Échec du chargement des projets", "error");
      const normalized = persData.map((p: any) => ({
        ...p,
        pains: Array.isArray(p.pains)
          ? p.pains
          : typeof p.pains === "string"
            ? p.pains
                .split(",")
                .map((x: string) => x.trim())
                .filter(Boolean)
            : [],
        goals: Array.isArray(p.goals)
          ? p.goals
          : typeof p.goals === "string"
            ? p.goals
                .split(",")
                .map((x: string) => x.trim())
                .filter(Boolean)
            : [],
      }));
      setPersonas(normalized);
      setProjects(projData);
    })();
  }, []);

  async function addProject() {
    if (!token || !newProjectName.trim()) return;
    try {
      const res = await apiFetch("/v1/projects", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newProjectName.trim() }),
      });
      const p = await res.json();
      if (!res.ok) return addToast(p.error || "Erreur", "error");
      setProjects((list) => [...list, p]);
      setProjectId(p.id);
      setShowProjectForm(false);
      setNewProjectName("");
    } catch (err) {
      console.error(err);
      addToast(err instanceof Error ? err.message : "Erreur", "error");
    }
  }

  return (
    <>
      <PageLayout maxWidth="full" className="space-y-6">
        <PageHeader
          title="Nouveau contenu"
          breadcrumbs={[{ label: "Contenus", href: "/contents" }, { label: "Nouveau" }]}
        />
        <form
          className="w-full space-y-6"
          onSubmit={handleSubmit(async (v: z.infer<typeof S>) => {
            try {
              setSubmitError(null);
              if (credits <= 0) {
                setShowCreditModal(true);
                return;
              }
              if (!v.persona?.name) delete v.persona;
              const session = await supabase.auth.getSession();
              const accessToken = session.data.session?.access_token;
              if (!accessToken) {
                handleSubmitError("Non authentifié");
                return;
              }
              const obj = {
                keyword: v.primaryKeyword.trim(),
                secondaryKeywords: Array.isArray(v.secondaryKeywords)
                  ? v.secondaryKeywords.map((x) => x.trim()).filter(Boolean)
                  : typeof v.secondaryKeywords === "string"
                    ? v.secondaryKeywords
                        .split(",")
                        .map((x: string) => x.trim())
                        .filter(Boolean)
                    : [],
                language: v.language.trim(),
                country: v.targetCountry.trim(),
                intent: v.searchIntent,
                type: v.contentType.trim(),
                imageCount: Number(v.imageCount),
                // Advanced SEO
                targetEntities: Array.isArray(v.targetEntities) ? v.targetEntities : [],
                competitorUrls: Array.isArray(v.competitorUrls) ? v.competitorUrls : [],
                serpFeatures: Array.isArray(v.serpFeatures) ? v.serpFeatures : [],
                cta: typeof (v as any).cta === "string" ? (v as any).cta : "",
                readingLevel: (v as any).readingLevel || "pro",
                requiredSections: Array.isArray((v as any).requiredSections)
                  ? (v as any).requiredSections
                  : [],
                forbidden: Array.isArray((v as any).forbidden) ? (v as any).forbidden : [],
                wordCount: typeof (v as any).wordCount === "object" ? (v as any).wordCount : {},
              };
              let input: CreateContent;
              try {
                input = CreateContentSchema.parse(obj);
              } catch (err) {
                handleSubmitError(err instanceof Error ? err.message : "Erreur");
                return;
              }
              const res = await apiFetch("/v1/contents", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ ...input, projectId: projectId || undefined }),
              });
              const data = await res.json();
              const id = data?.id as string | undefined;
              const isUuid = typeof id === "string" && /^[0-9a-fA-F-]{36}$/.test(id);
              if ((res.status === 200 || res.status === 201) && isUuid) {
                addToast("Contenu créé. Lancement de la génération…", "success");
                // Optionnel: déclencher une SERP pour déduire l'intention
                if (serpAuto) {
                  try {
                    const serpRes = await apiFetch(`/v1/serp/fetch`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                      },
                      body: JSON.stringify({
                        keyword: v.primaryKeyword,
                        language: v.language,
                        country: v.targetCountry,
                      }),
                    });
                    const serp = await serpRes.json().catch(() => ({}));
                    if (serpRes.ok && serp && (serp.serp_query_id || serp.analysis)) {
                      const patch: any = {};
                      if (serp.serp_query_id) patch.serp_query_id = serp.serp_query_id;
                      if (serp.analysis?.dominant_intent) {
                        patch.intent_resolved = serp.analysis.dominant_intent;
                        patch.intent_source = "serp_auto";
                      }
                      if (Object.keys(patch).length) {
                        await apiFetch(`/v1/contents/${id}`, {
                          method: "PATCH",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${accessToken}`,
                          },
                          body: JSON.stringify(patch),
                        });
                      }
                    }
                  } catch {
                    /* ignore */
                  }
                }
                void apiFetch("/v1/generate/batch", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify({ contentId: id, steps: ["outline", "meta", "score"] }),
                });
                await refresh();
                localStorage.removeItem(FORM_STORAGE_KEY);
                nav(`/content/${id}`);
              } else if (res.status >= 400 || !id) {
                handleSubmitError(
                  data.error
                    ? `${data.error}${data.requestId ? ` (id: ${data.requestId})` : ""}`
                    : "Erreur",
                );
                return;
              } else {
                handleSubmitError("Erreur");
              }
            } catch (err) {
              console.error(err);
              handleSubmitError(err instanceof Error ? err.message : "Erreur");
            }
          })}
        >
          {/* Header moved to PageHeader */}

          {/* Playbooks */}
          <div className="mb-2 flex items-center gap-2">
            <Select value={playbook} onChange={(e) => setPlaybook(e.target.value)}>
              <option value="">Choisir un playbook…</option>
              {PLAYBOOKS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </Select>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const pb = PLAYBOOKS.find((p) => p.id === playbook);
                if (!pb) return;
                pb.apply(setValue);
                addToast(`Playbook « ${pb.label} » appliqué`);
              }}
            >
              Appliquer
            </Button>
          </div>

          {/* Stepper */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {steps.map((s, i) => (
                <div key={s} className="flex flex-1 flex-col items-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border ${
                      i <= step ? "bg-text text-bg" : "bg-surface text-muted"
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span className="mt-1 text-xs">{s}</span>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                reset(DEFAULT_FORM_VALUES);
                setPersonaId("");
                setProjectId("");
                setShowProjectForm(false);
                setNewProjectName("");
                setPlaybook("");
                setSerpAuto(true);
                setStep(0);
                localStorage.removeItem(FORM_STORAGE_KEY);
              }}
            >
              Réinitialiser
            </Button>
          </div>

          {step === 0 && (
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <Type className="h-6 w-6 text-muted" />
                  <FormField
                    label="Mot‑clé principal"
                    htmlFor="primaryKeyword"
                    required
                    error={errors.primaryKeyword?.message}
                    className="flex-1"
                  >
                    <Input
                      id="primaryKeyword"
                      placeholder="Titre ou mot‑clé principal"
                      required
                      isValid={!!values.primaryKeyword && !errors.primaryKeyword}
                      hasError={!!errors.primaryKeyword}
                      {...register("primaryKeyword")}
                    />
                  </FormField>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <List className="h-6 w-6 text-muted" />
                  <FormField
                    label="Mots‑clés secondaires"
                    htmlFor="secondaryKeywords"
                    help="Séparez par des virgules ou des retours à la ligne (max 5)"
                    error={errors.secondaryKeywords?.message as string | undefined}
                    className="flex-1"
                  >
                    <Controller
                      name="secondaryKeywords"
                      control={control}
                      rules={{
                        validate: (v) => (v?.length ?? 0) <= 5 || "Maximum 5 mots-clés",
                      }}
                      render={({ field }) => {
                        const listValue = Array.isArray(field.value) ? field.value : [];
                        return (
                          <Input
                            id="secondaryKeywords"
                            placeholder="ex: audit seo, contenu b2b"
                            value={joinListInput(listValue)}
                            onChange={(e) => field.onChange(parseListInput(e.target.value))}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            isValid={listValue.length > 0 && !errors.secondaryKeywords}
                            hasError={!!errors.secondaryKeywords}
                          />
                        );
                      }}
                    />
                  </FormField>
                </Card>
              </motion.div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={async () => {
                    const valid = await trigger(["primaryKeyword", "secondaryKeywords"]);
                    notifyStepValidation("Contenu", valid);
                    if (valid) setStep(1);
                  }}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <Palette className="h-6 w-6 text-muted" />
                  <div className="flex flex-1 flex-col">
                    <label className="text-sm font-medium">Langue</label>
                    <Select
                      value={values.language}
                      onChange={(e) => setValue("language", e.target.value)}
                    >
                      {Object.entries(LANGUAGES).map(([code, { label, flag }]) => (
                        <option key={code} value={code}>
                          {flag} {label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <Users className="h-6 w-6 text-muted" />
                  <div className="flex flex-1 flex-col">
                    <label className="text-sm font-medium">Pays cible</label>
                    <Select
                      value={values.targetCountry}
                      onChange={(e) => setValue("targetCountry", e.target.value)}
                    >
                      {Object.entries(COUNTRIES).map(([code, { label, flag }]) => (
                        <option key={code} value={code}>
                          {flag} {label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <Briefcase className="h-6 w-6 text-muted" />
                  <div className="flex flex-1 items-center gap-2">
                    <Select
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      className="flex-1"
                    >
                      <option value="">Sans projet</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowProjectForm((s) => !s)}
                      className="shrink-0 px-2"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
                {showProjectForm && (
                  <div className="mt-2 flex items-center gap-2">
                    <FormField
                      label="Nom du projet"
                      htmlFor="project-name-inline"
                      className="flex-1"
                    >
                      <Input
                        id="project-name-inline"
                        placeholder="Nom du projet"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                      />
                    </FormField>
                    <Button
                      type="button"
                      disabled={!newProjectName.trim()}
                      onClick={() => void addProject()}
                    >
                      Ajouter
                    </Button>
                  </div>
                )}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <User className="h-6 w-6 text-muted" />
                  <Select
                    value={personaId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setPersonaId(id);
                      const p = personas.find((x) => x.id === id);
                      setValue("persona", p);
                    }}
                  >
                    <option value="">Sans persona</option>
                    {personas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <Ruler className="h-6 w-6 text-muted" />
                  <div className="flex flex-1 flex-col">
                    <label className="text-sm font-medium">Intention de recherche</label>
                    <Select
                      value={values.searchIntent}
                      onChange={(e) => setValue("searchIntent", e.target.value)}
                    >
                      <option value="informational">Information</option>
                      <option value="commercial">Commercial</option>
                      <option value="transactional">Transaction</option>
                      <option value="navigational">Navigation</option>
                    </Select>
                  </div>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <Type className="h-6 w-6 text-muted" />
                  <div className="flex flex-1 flex-col">
                    <label className="text-sm font-medium">Type de contenu</label>
                    <Select
                      value={values.contentType}
                      onChange={(e) => setValue("contentType", e.target.value)}
                    >
                      <option value="article">Article</option>
                      <option value="landing">Landing page</option>
                    </Select>
                  </div>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <ImageIcon className="h-6 w-6 text-muted" />
                  <div className="flex flex-1 flex-col">
                    <label className="text-sm font-medium">Nombre d'images</label>
                    <Select
                      value={values.imageCount}
                      onChange={(e) => setValue("imageCount", parseInt(e.target.value))}
                    >
                      {Array.from({ length: 9 }, (_, i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </Select>
                  </div>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="space-y-4 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Settings className="h-6 w-6 text-muted" />
                      <div>
                        <h3 className="font-semibold">Options avancées</h3>
                        <p className="text-xs text-muted">
                          Affinez la génération pour coller à votre SERP.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="px-3 py-1 text-sm"
                      onClick={() => setShowAdvanced((s) => !s)}
                    >
                      {showAdvanced ? "Masquer" : "Afficher"}
                    </Button>
                  </div>
                  {showAdvanced && (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="flex items-center gap-2 md:col-span-2">
                        <input
                          id="serp-auto"
                          type="checkbox"
                          checked={serpAuto}
                          onChange={(e) => setSerpAuto(e.target.checked)}
                        />
                        <label htmlFor="serp-auto" className="text-sm">
                          Déduire automatiquement l'intention depuis la SERP
                        </label>
                      </div>
                      <div className="flex flex-col gap-2 rounded-md border border-muted/30 p-3 md:col-span-2">
                        <span className="text-sm font-medium">Structure</span>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={values.withFaq ?? false}
                            onChange={(e) => setValue("withFaq", e.target.checked)}
                          />
                          Inclure une FAQ
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={values.useFormatting ?? false}
                            onChange={(e) => setValue("useFormatting", e.target.checked)}
                          />
                          Activer la mise en forme enrichie
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={values.autoMeta ?? false}
                            onChange={(e) => setValue("autoMeta", e.target.checked)}
                          />
                          Générer automatiquement les métadonnées
                        </label>
                      </div>
                      <FormField
                        label="Entités cibles"
                        htmlFor="target-entities"
                        help="Séparez par virgule ou retour à la ligne"
                      >
                        <Controller
                          name="targetEntities"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="target-entities"
                              placeholder="ex: Google, Core Web Vitals"
                              value={joinListInput(field.value)}
                              onChange={(e) => field.onChange(parseListInput(e.target.value))}
                              onBlur={field.onBlur}
                              ref={field.ref}
                            />
                          )}
                        />
                      </FormField>
                      <FormField
                        label="URLs concurrentes"
                        htmlFor="competitor-urls"
                        help="Une URL par ligne ou séparées par des virgules"
                      >
                        <Controller
                          name="competitorUrls"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="competitor-urls"
                              placeholder="ex: https://exemple.com/a"
                              value={Array.isArray(field.value) ? field.value.join(", ") : ""}
                              onChange={(e) => field.onChange(parseListInput(e.target.value))}
                              onBlur={field.onBlur}
                              ref={field.ref}
                            />
                          )}
                        />
                      </FormField>
                      <FormField
                        label="Features SERP"
                        htmlFor="serp-features"
                        help="Maintenez Cmd/Ctrl pour une sélection multiple"
                      >
                        <Controller
                          name="serpFeatures"
                          control={control}
                          render={({ field }) => (
                            <Select
                              id="serp-features"
                              multiple
                              value={Array.isArray(field.value) ? field.value : []}
                              onChange={(e) =>
                                field.onChange(
                                  Array.from(e.target.selectedOptions).map((o) => o.value),
                                )
                              }
                              onBlur={field.onBlur}
                              ref={field.ref}
                            >
                              <option value="featured_snippet">Featured snippet</option>
                              <option value="paa">People Also Ask</option>
                              <option value="images">Images</option>
                              <option value="videos">Vidéos</option>
                              <option value="news">News</option>
                            </Select>
                          )}
                        />
                      </FormField>
                      <FormField label="CTA principal" htmlFor="cta" help="Ex: Demander une démo">
                        <Controller
                          name="cta"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="cta"
                              placeholder="ex: Demander une démo"
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              ref={field.ref}
                            />
                          )}
                        />
                      </FormField>
                      <FormField label="Niveau de lecture" htmlFor="reading-level">
                        <Select
                          id="reading-level"
                          value={values.readingLevel || "pro"}
                          onChange={(e) => setValue("readingLevel", e.target.value)}
                        >
                          <option value="simple">Simple</option>
                          <option value="pro">Pro</option>
                          <option value="expert">Expert</option>
                        </Select>
                      </FormField>
                      <FormField
                        label="Sections obligatoires"
                        htmlFor="required-sections"
                        help="Séparez par virgule ou retour à la ligne"
                      >
                        <Controller
                          name="requiredSections"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="required-sections"
                              placeholder="ex: Tarifs, Comparatif"
                              value={joinListInput(field.value)}
                              onChange={(e) => field.onChange(parseListInput(e.target.value))}
                              onBlur={field.onBlur}
                              ref={field.ref}
                            />
                          )}
                        />
                      </FormField>
                      <FormField
                        label="À éviter"
                        htmlFor="forbidden"
                        help="Séparez par virgule ou retour à la ligne"
                      >
                        <Controller
                          name="forbidden"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="forbidden"
                              placeholder="ex: Meilleur, N°1"
                              value={joinListInput(field.value)}
                              onChange={(e) => field.onChange(parseListInput(e.target.value))}
                              onBlur={field.onBlur}
                              ref={field.ref}
                            />
                          )}
                        />
                      </FormField>
                      <div className="grid grid-cols-2 gap-3 md:col-span-2">
                        <FormField label="Mots min" htmlFor="wordcount-min">
                          <Controller
                            name="wordCount.min"
                            control={control}
                            render={({ field }) => (
                              <Input
                                id="wordcount-min"
                                type="number"
                                min={0}
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value ? parseInt(e.target.value, 10) : undefined,
                                  )
                                }
                                onBlur={field.onBlur}
                                ref={field.ref}
                              />
                            )}
                          />
                        </FormField>
                        <FormField label="Mots max" htmlFor="wordcount-max">
                          <Controller
                            name="wordCount.max"
                            control={control}
                            render={({ field }) => (
                              <Input
                                id="wordcount-max"
                                type="number"
                                min={0}
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value ? parseInt(e.target.value, 10) : undefined,
                                  )
                                }
                                onBlur={field.onBlur}
                                ref={field.ref}
                              />
                            )}
                          />
                        </FormField>
                      </div>
                    </div>
                  )}
                </Card>
              </motion.div>
              <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(0)}>
                  Précédent
                </Button>
                <Button
                  type="button"
                  onClick={async () => {
                    const valid = await trigger([
                      "language",
                      "targetCountry",
                      "searchIntent",
                      "contentType",
                      "imageCount",
                    ]);
                    notifyStepValidation("Options", valid);
                    if (valid) setStep(2);
                  }}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-semibold">Récapitulatif</h2>
                </CardHeader>
                <CardContent>
                  <dl className="divide-y divide-muted/20 text-sm">
                    <div className="flex items-center justify-between py-2">
                      <dt className="font-medium text-muted">Mot-clé principal</dt>
                      <dd className="text-text">{values.primaryKeyword}</dd>
                    </div>
                    {Array.isArray(values.secondaryKeywords) &&
                      values.secondaryKeywords.length > 0 && (
                        <div className="flex items-center justify-between py-2">
                          <dt className="font-medium text-muted">Mots-clés secondaires</dt>
                          <dd className="text-text">{values.secondaryKeywords.join(", ")}</dd>
                        </div>
                      )}
                    {selectedProject && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Projet</dt>
                        <dd className="text-text">{selectedProject.name}</dd>
                      </div>
                    )}
                    {values.persona?.name && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Persona</dt>
                        <dd className="text-text">{values.persona.name}</dd>
                      </div>
                    )}
                    <div className="flex items-center justify-between py-2">
                      <dt className="font-medium text-muted">Langue</dt>
                      <dd className="text-text">
                        {LANGUAGES[values.language as keyof typeof LANGUAGES]?.flag}{" "}
                        {LANGUAGES[values.language as keyof typeof LANGUAGES]?.label}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <dt className="font-medium text-muted">Pays cible</dt>
                      <dd className="text-text">
                        {COUNTRIES[values.targetCountry as keyof typeof COUNTRIES]?.flag}{" "}
                        {COUNTRIES[values.targetCountry as keyof typeof COUNTRIES]?.label}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <dt className="font-medium text-muted">Intention de recherche</dt>
                      <dd className="text-text">{getIntentLabel(values.searchIntent) || "—"}</dd>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <dt className="font-medium text-muted">Type de contenu</dt>
                      <dd className="text-text">
                        {getContentTypeLabel(values.contentType) || "—"}
                      </dd>
                    </div>
                    {values.imageCount > 0 && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Nombre d'images</dt>
                        <dd className="text-text">{values.imageCount}</dd>
                      </div>
                    )}
                    <div className="flex items-center justify-between py-2">
                      <dt className="font-medium text-muted">Analyse SERP</dt>
                      <dd className="text-text">{serpAuto ? "Automatique" : "Manuelle"}</dd>
                    </div>
                    {values.readingLevel && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Niveau de lecture</dt>
                        <dd className="text-text">
                          {getReadingLevelLabel(values.readingLevel) || values.readingLevel}
                        </dd>
                      </div>
                    )}
                    {values.withFaq && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">FAQ</dt>
                        <dd className="text-text">Incluse</dd>
                      </div>
                    )}
                    {values.useFormatting && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Mise en forme</dt>
                        <dd className="text-text">Enrichie</dd>
                      </div>
                    )}
                    {values.autoMeta && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Métadonnées</dt>
                        <dd className="text-text">Générées automatiquement</dd>
                      </div>
                    )}
                    {values.cta?.trim() && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">CTA principal</dt>
                        <dd className="text-text">{values.cta}</dd>
                      </div>
                    )}
                    {Array.isArray(values.targetEntities) && values.targetEntities.length > 0 && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Entités cibles</dt>
                        <dd className="text-text">{values.targetEntities.join(", ")}</dd>
                      </div>
                    )}
                    {Array.isArray(values.competitorUrls) && values.competitorUrls.length > 0 && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">URLs concurrentes</dt>
                        <dd className="text-text">{values.competitorUrls.join(", ")}</dd>
                      </div>
                    )}
                    {Array.isArray(values.serpFeatures) && values.serpFeatures.length > 0 && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Features SERP ciblées</dt>
                        <dd className="text-text">
                          {values.serpFeatures
                            .map((feature) => getSerpFeatureLabel(feature))
                            .join(", ")}
                        </dd>
                      </div>
                    )}
                    {Array.isArray(values.requiredSections) &&
                      values.requiredSections.length > 0 && (
                        <div className="flex items-center justify-between py-2">
                          <dt className="font-medium text-muted">Sections obligatoires</dt>
                          <dd className="text-text">{values.requiredSections.join(", ")}</dd>
                        </div>
                      )}
                    {Array.isArray(values.forbidden) && values.forbidden.length > 0 && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">À éviter</dt>
                        <dd className="text-text">{values.forbidden.join(", ")}</dd>
                      </div>
                    )}
                    {(values.wordCount?.min || values.wordCount?.max) && (
                      <div className="flex items-center justify-between py-2">
                        <dt className="font-medium text-muted">Objectif de longueur</dt>
                        <dd className="text-text">
                          {values.wordCount?.min ? `${values.wordCount.min} mots min` : null}
                          {values.wordCount?.min && values.wordCount?.max ? " – " : null}
                          {values.wordCount?.max ? `${values.wordCount.max} mots max` : null}
                        </dd>
                      </div>
                    )}
                  </dl>
                </CardContent>
              </Card>
              <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Précédent
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Générer
                </Button>
              </div>
              {submitError && <p className="mt-2 text-sm text-danger">{submitError}</p>}
              <p className="text-sm text-muted">
                Crédits restants : {credits} - <Link to="/credits">Acheter des crédits</Link>
              </p>
            </div>
          )}
        </form>
      </PageLayout>
      {isSubmitting && <LoadingOverlay message="Création du contenu..." />}
      <AlertModal
        open={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        title="Crédits insuffisants"
        description="Vous n'avez pas assez de crédits pour lancer la rédaction."
        action={{
          label: "Acheter des crédits",
          onClick: () => {
            setShowCreditModal(false);
            nav("/credits");
          },
        }}
      />
      <Toasts />
    </>
  );
}
